/**
 * Type-Safe Error Codes for Ory IAM Library
 *
 * Comprehensive type system for all error codes used across the library:
 * 1. Ory semantic codes (from message IDs)
 * 2. HTTP status codes
 * 3. Axios network codes
 * 4. Custom flow codes
 *
 * Note: OrySemanticErrorCode is exported from ../utils to avoid circular dependencies.
 */

/**
 * Axios network error codes
 *
 * Source: https://axios-http.com/docs/handling_errors
 */
export type AxiosErrorCode =
  | 'ERR_NETWORK'         // Network error (no response)
  | 'ERR_CANCELED'        // Request was cancelled
  | 'ECONNABORTED'        // Connection aborted
  | 'ETIMEDOUT'           // Request timeout
  | 'ERR_BAD_REQUEST'     // Bad request (4xx)
  | 'ERR_BAD_RESPONSE';   // Bad response (5xx)

/**
 * HTTP status codes as semantic strings
 *
 * Common HTTP status codes used by Ory Kratos
 */
export type HttpStatusCode =
  | 'bad_request'         // 400
  | 'unauthorized'        // 401
  | 'forbidden'           // 403
  | 'not_found'           // 404
  | 'gone'                // 410
  | 'unprocessable_entity' // 422
  | 'internal_server_error' // 500
  | 'bad_gateway'         // 502
  | 'service_unavailable' // 503
  | 'gateway_timeout';    // 504

/**
 * Custom flow-related error codes
 *
 * These are library-specific codes for flow lifecycle errors
 */
export type FlowErrorCode =
  | 'flow_expired'        // Flow has expired (generic)
  | 'flow_validation_error'; // Flow validation failed (generic)

/**
 * Ory service error codes
 *
 * Semantic error IDs provided by Ory in error envelopes.
 * Common examples (non-exhaustive):
 */
export type OryServiceErrorCode =
  | 'session_inactive'           // No active session (401)
  | 'session_aal1_required'      // AAL1 required
  | 'session_aal2_required'      // AAL2 required
  | 'security_csrf_violation'    // CSRF violation
  | 'security_identity_mismatch' // Identity mismatch
  | string;                      // Ory may return other error IDs

/**
 * Fallback HTTP error codes for unknown status codes
 *
 * Format: "http_{status_code}"
 * Example: "http_401", "http_500", "http_unknown"
 */
export type HttpFallbackCode = `http_${number | 'unknown'}`;

/**
 * Re-export OrySemanticErrorCode from utils for convenience
 *
 * Note: This is defined in ../utils/ory-message-id-map.ts to avoid circular dependencies.
 */
export type { OrySemanticErrorCode } from '../../i18n';

/**
 * Type guard to check if a code is an Axios error code
 */
export function isAxiosErrorCode(code: string | undefined): code is AxiosErrorCode {
  if (!code) return false;
  return (
    code === 'ERR_NETWORK' ||
    code === 'ERR_CANCELED' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_BAD_REQUEST' ||
    code === 'ERR_BAD_RESPONSE'
  );
}

/**
 * Type guard to check if a code is an HTTP fallback code
 */
export function isHttpFallbackCode(code: string): code is HttpFallbackCode {
  return /^http_(\d+|unknown)$/.test(code);
}

